<lib-list-page
    [host]="this"
    title="User Data"
    icon="file_copy"
    searchPlaceholder="Search user data…"
    [activeChips]="activeFilterChips()"
    (chipRemove)="removeFilterChip($event)"
    totalIcon="file_copy"
    totalLabel="Total"
    emptyStateIcon="file_copy"
    emptyStateTitle="No user data found"
    emptyStateSubtitle="No user data records match the current filter.">

  <ng-container actions>
    <button *ngIf="permissionChecker.hasAccessToCreateUserData()"
            mat-flat-button color="primary" class="page-header-primary-action"
            (click)="onCreate()" matTooltip="Create a UserData row">
      <mat-icon>add</mat-icon><span>Add User Data</span>
    </button>
    <button *ngIf="permissionChecker.hasAccessToPurgeUserData()"
            mat-stroked-button class="page-header-secondary-action"
            (click)="onPurgeDataByType()" matTooltip="Purge by type">
      <mat-icon>delete_sweep</mat-icon><span>Purge By Type</span>
    </button>
    <button *ngIf="permissionChecker.hasAccessToPurgeUserData()"
            mat-stroked-button color="warn" class="page-header-secondary-action"
            (click)="onPurgeAllData()" matTooltip="Purge all data">
      <mat-icon>delete_forever</mat-icon><span>Purge All</span>
    </button>
    <button mat-icon-button (click)="refreshData(true)" matTooltip="Refresh" aria-label="Refresh">
      <mat-icon>refresh</mat-icon>
    </button>
  </ng-container>

  <ng-container filters>
    <div class="filter-select-box">
      <div class="filter-context-label">
        <mat-icon class="filter-context-icon">category</mat-icon> Kind
      </div>
      <div class="filter-checkboxes">
        <mat-checkbox [checked]="filter().resource"    (change)="setBoolField('resource',    $event.checked)">Resource</mat-checkbox>
        <mat-checkbox [checked]="filter().notResource" (change)="setBoolField('notResource', $event.checked)">Non-resource</mat-checkbox>
      </div>
    </div>

    <div class="filter-select-box">
      <div class="filter-context-label">
        <mat-icon class="filter-context-icon">label</mat-icon> Data Types
        <span class="filter-active-count" *ngIf="filter().dataTypes?.length as count">{{ count }}</span>
      </div>
      <lib-filter-autocomplete
        #dataTypeFilter
        [source]="dataTypeList"
        [filterSignal]="filter"
        field="dataTypes"
        chipIcon="label"
        label="Data Types" />
    </div>

    <div class="filter-select-box">
      <div class="filter-context-label">
        <mat-icon class="filter-context-icon">account_balance</mat-icon> Financial Units
        <span class="filter-active-count" *ngIf="filter().financialUnits?.length as count">{{ count }}</span>
      </div>
      <lib-filter-autocomplete
        #financialUnitFilter
        [source]="financialUnitList"
        [filterSignal]="filter"
        field="financialUnits"
        chipIcon="account_balance"
        label="Financial Units" />
    </div>

    <div class="filter-select-box">
      <div class="filter-context-label">
        <mat-icon class="filter-context-icon">corporate_fare</mat-icon> Org Units
        <span class="filter-active-count" *ngIf="filter().orgUnits?.length as count">{{ count }}</span>
      </div>
      <lib-filter-autocomplete
        #orgUnitFilter
        [source]="orgUnitList"
        [filterSignal]="filter"
        field="orgUnits"
        chipIcon="corporate_fare"
        label="Org Units" />
    </div>

    <div class="filter-select-box">
      <div class="filter-context-label">
        <mat-icon class="filter-context-icon">groups</mat-icon> Departments
        <span class="filter-active-count" *ngIf="filter().departments?.length as count">{{ count }}</span>
      </div>
      <lib-filter-autocomplete
        #departmentFilter
        [source]="departmentList"
        [filterSignal]="filter"
        field="departments"
        chipIcon="groups"
        label="Departments" />
    </div>
  </ng-container>

  <table mat-table table [dataSource]="dataSource()" class="data-table" matSort (matSortChange)="sortData($event)">

    <ng-container matColumnDef="type">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="type" class="nowrap">Type</th>
      <td mat-cell *matCellDef="let element" class="nowrap">
        <a *ngIf="permissionChecker.hasAccessToUserDataTypeManagement(); else typeText"
           class="traversable" (click)="onOpenType(element)">{{ element.type }}</a>
        <ng-template #typeText>{{ element.type }}</ng-template>
      </td>
    </ng-container>

    <ng-container matColumnDef="key">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="key" class="nowrap">Key</th>
      <td mat-cell *matCellDef="let element" class="nowrap">{{ element.key }}</td>
    </ng-container>

    <ng-container matColumnDef="label">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="label" class="nowrap">Label</th>
      <td mat-cell *matCellDef="let element" class="nowrap">
        <a *ngIf="permissionChecker.hasAccessToUpdateUserData() && element.label; else labelPlain"
           class="traversable" (click)="onEdit(element)" matTooltip="Edit">{{ element.label }}</a>
        <ng-template #labelPlain>
          <span *ngIf="element.label; else labelEmpty">{{ element.label }}</span>
          <ng-template #labelEmpty><span class="muted">—</span></ng-template>
        </ng-template>
      </td>
    </ng-container>

    <ng-container matColumnDef="resource">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="resource" class="nowrap">Resource</th>
      <td mat-cell *matCellDef="let element" class="nowrap resource-cell"
          [matTooltip]="resourceTooltip(element)">
        <ng-container *ngIf="isResourceRow(element); else nonResource">
          <span class="resource-badge"
                [class.pool]="element.reusable !== false"
                [class.burn-down]="element.reusable === false">
              <mat-icon class="resource-icon"
                        [class.burn-down]="element.reusable === false">{{ resourceIcon(element) }}</mat-icon>
              <span class="resource-badge-label">{{ element.reusable === false ? 'Burn-down' : 'Pool' }}</span>
          </span>
          <span class="resource-capacity" *ngIf="formatCapacity(element) as cap">{{ cap }}</span>
          <span class="resource-sep" *ngIf="formatAvailability(element)">·</span>
          <span class="resource-window" *ngIf="formatAvailability(element) as win">{{ win }}</span>
          <span class="resource-sep" *ngIf="formatCost(element)">·</span>
          <span class="resource-cost" *ngIf="formatCost(element) as cost">{{ cost }}</span>
        </ng-container>
        <ng-template #nonResource>
          <span class="resource-badge non-resource">
              <mat-icon class="non-resource-icon">do_not_disturb_on_outline</mat-icon>
              <span class="resource-badge-label">Standalone</span>
          </span>
        </ng-template>
      </td>
    </ng-container>

    <ng-container matColumnDef="createTimestamp">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="createTimestamp" class="nowrap">Created</th>
      <td mat-cell *matCellDef="let element" class="nowrap">{{ element.createTimestamp | date:'medium' }}</td>
    </ng-container>

    <ng-container matColumnDef="lastUpdateTimestamp">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="lastUpdateTimestamp" class="nowrap">Last Updated</th>
      <td mat-cell *matCellDef="let element" class="nowrap">{{ element.lastUpdateTimestamp | date:'medium' }}</td>
    </ng-container>

    <ng-container matColumnDef="star" stickyEnd>
      <th mat-header-cell *matHeaderCellDef aria-label="row actions">&nbsp;</th>
      <td mat-cell *matCellDef="let element" class="nowrap row-actions">
        <button mat-icon-button (click)="onViewData(element)" matTooltip="View details" aria-label="View details">
          <mat-icon>view_comfy</mat-icon>
        </button>
        <!-- Reset capacity — only burn-down resources with declared capacity are eligible. -->
        <button *ngIf="permissionChecker.hasAccessToUpdateUserData() && isResettableBurnDown(element)"
                mat-icon-button (click)="onResetCapacity(element)"
                matTooltip="Reset capacity (burn-down restock)" aria-label="Reset capacity">
          <mat-icon>restart_alt</mat-icon>
        </button>
        <button *ngIf="permissionChecker.hasAccessToDeleteUserData()"
                mat-icon-button (click)="onDelete(element)" matTooltip="Delete" aria-label="Delete user data"
                class="row-action-danger">
          <mat-icon>delete</mat-icon>
        </button>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns" class="selectable-table-row"></tr>
  </table>

</lib-list-page>
